/**
 * flickity-a11y.js
 *
 * Keeps tabindex in sync with Flickity's aria-hidden state across all
 * carousels on the page (slideshow, featured collection, product thumbnails).
 *
 * When Flickity hides a cell with aria-hidden="true", all focusable
 * descendants are removed from the tab order. When the cell becomes
 * visible again, their original tabindex values are restored.
 */
(function () {
  const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
  const STORED_ATTR = 'data-a11y-tabindex';

  function disableFocus(cell) {
    // Remove focusable descendants from tab order, storing original tabindex
    cell.querySelectorAll(FOCUSABLE).forEach(function (el) {
      if (!el.hasAttribute(STORED_ATTR)) {
        el.setAttribute(STORED_ATTR, el.getAttribute('tabindex') ?? '');
      }
      el.setAttribute('tabindex', '-1');
    });

    // Handle the cell element itself when it carries an explicit tabindex="0"
    // (e.g. product thumbnail divs)
    if (cell.getAttribute('tabindex') === '0' && !cell.hasAttribute(STORED_ATTR)) {
      cell.setAttribute(STORED_ATTR, '0');
      cell.setAttribute('tabindex', '-1');
    }
  }

  function enableFocus(cell) {
    // Restore focusable descendants to their original tab order
    cell.querySelectorAll('[' + STORED_ATTR + ']').forEach(function (el) {
      const original = el.getAttribute(STORED_ATTR);
      original === '' ? el.removeAttribute('tabindex') : el.setAttribute('tabindex', original);
      el.removeAttribute(STORED_ATTR);
    });

    // Restore the cell element itself
    if (cell.hasAttribute(STORED_ATTR)) {
      cell.setAttribute('tabindex', cell.getAttribute(STORED_ATTR));
      cell.removeAttribute(STORED_ATTR);
    }
  }

  function syncCell(cell) {
    cell.getAttribute('aria-hidden') === 'true' ? disableFocus(cell) : enableFocus(cell);
  }

  // --- Cart button aria-label ---
  // Keeps the cart trigger's aria-label in sync with the live item count.
  function updateCartLabel(countEl) {
    const btn = document.querySelector('.js-mini-cart-trigger[data-cart-label-base]');
    if (!btn) return;

    const count = parseInt(countEl.textContent.trim(), 10) || 0;
    const base = btn.getAttribute('data-cart-label-base');
    btn.setAttribute('aria-label', base + ', ' + count + ' ' + (count === 1 ? 'item' : 'items'));
  }

  document.addEventListener('DOMContentLoaded', function () {
    // Watch the cart count span for text content changes (e.g. after AJAX add-to-cart)
    const cartCountEl = document.querySelector('.js-cart-count');
    if (cartCountEl) {
      new MutationObserver(function () {
        updateCartLabel(cartCountEl);
      }).observe(cartCountEl, { childList: true, subtree: true, characterData: true });
    }

    // Initial pass: Flickity has already run by this point (theme.js is
    // synchronous), so fix any cells it has already marked as hidden.
    document.querySelectorAll('.flickity-slider > [aria-hidden="true"]').forEach(disableFocus);

    // Watch for future aria-hidden changes on carousel cells across the page.
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.attributeName !== 'aria-hidden') return;

        const cell = mutation.target;
        if (!cell.parentElement || !cell.parentElement.classList.contains('flickity-slider')) return;

        syncCell(cell);
      });
    }).observe(document.body, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      subtree: true,
    });
  });
})();
